<template>
  <v-container fluid class="portal-page resource-grid">
    <v-btn v-if="currentCustomer" :to="{ name: 'CustomerDetails', params: { customerId: currentCustomer.customerId } }" variant="text" prepend-icon="mdi-arrow-left" class="mb-3">{{ currentCustomer.customerName }}</v-btn>
    <v-row>
      <v-col cols="12">
        <v-card-title class="d-flex justify-space-between align-center flex-nowrap">
          <div>
            <div class="text-h4 font-weight-bold mb-2">Sites</div>
            <div class="subtitle-2 grey--text">Sites belonging to {{ currentCustomer?.customerName || 'this customer' }}. Open a site to manage its tours.</div>
          </div>
          <div>
            <v-btn color="success" dark small flat @click="onAddSite">
              <v-icon left size="18">mdi-plus</v-icon>
              Create New
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col v-for="site in sites" :key="site.id" cols="12" md="6">
        <v-card elevation="4" class="site-card" >
          <v-col class="card-details pa-4 d-flex flex-column" cols="5" style="max-width: 41.6667%">
            <div class="text-h5 font-weight-bold mb-10">
              {{ site.name }}
            </div>
            <p class="text-body-2 mb-3">Manage this location's tours and visitor addresses.</p>
            <p class="text-caption">Updated {{ formatDate(site.updated) || '?' }}</p>
            <v-spacer />
            <SiteHostnames :site="site" class="mb-3" />
            <div class="card-actions">
              <v-btn color="primary" small @click="onViewDetails(site.id)">
                View Details →
              </v-btn>
              <v-btn
                text
                small
                color="error"
                class="ml-2"
                @click="onDeleteSite(site)"
              >
                Delete
              </v-btn>
            </div>
          </v-col>

          <v-col cols="7" class="card-map pa-0 d-flex">
            <img
              v-if="site.coverImage"
              class="coverImage"
              :src="site.coverImage"
              :alt="site.name"
            />
            <div v-else class="coverPlaceholder">Image here when available.</div>
          </v-col>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="showCreateDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">Create Site</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newSiteName"
            label="Site name"
            outlined
            dense
            :disabled="createBusy"
          />
          <v-text-field
            v-model="newSiteSubdomain"
            label="Visitor subdomain"
            placeholder="quin"
            :suffix="visitorDomainSuffix"
            outlined
            dense
            hint="This must be unique. Reserved names such as admin, api and www cannot be used."
            persistent-hint
            :disabled="createBusy"
            @blur="cleanNewSiteSubdomain"
            @update:model-value="newSiteSubdomainTouched = true"
          />
          <v-text-field
            v-model="newSiteSlug"
            label="Slug (optional)"
            outlined
            dense
            hint="Leave blank to auto-generate from the name."
            persistent-hint
            :disabled="createBusy"
          />
          <div v-if="createError" class="error--text caption">{{ createError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeCreateDialog" :disabled="createBusy">Cancel</v-btn>
          <v-btn color="success" dark :disabled="!newSiteName.trim() || !newSiteSubdomain.trim()" @click="submitCreateSite" :loading="createBusy">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { showConfirm, showMessage } from '@/utils/messages';

import { customerForUser } from '@/utils/customerSelection';
import { useAuthStore } from '@/stores/auth';
import SiteHostnames from '@/components/SiteHostnames.vue';
import { fetchSites, createSite, addSiteHostname, deleteSite } from "@/api/sites";
import { fetchTours } from "@/api/tours";
import {
  buildVisitorHostname,
  isReservedVisitorSubdomain,
  isValidVisitorSubdomain,
  normalizeVisitorSubdomain,
  suggestVisitorSubdomain,
  visitorDomainSuffix,
} from '@/utils/visitorHostnames';

export default {
  components: { SiteHostnames },
  data() {
    return {
      sites: [],
      showCreateDialog: false,
      newSiteName: "",
      newSiteSubdomain: "",
      newSiteSubdomainTouched: false,
      newSiteSlug: "",
      createBusy: false,
      createError: "",
      visitorDomainSuffix,
    };
  },

  mounted() {
    this.loadSites();
  },
  computed: { currentCustomer() { return customerForUser(useAuthStore().user?.profile?.sub); } },
  watch: {
    newSiteName(value) {
      if (!this.showCreateDialog || this.newSiteSubdomainTouched) return;
      this.newSiteSubdomain = suggestVisitorSubdomain(value);
    },
  },
  methods: {
    async loadSites() {
      try {
        const sitesData = await fetchSites();
        this.sites = sitesData;
      } catch (err) {
        console.error("Error fetching zones:", err);
      }
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    onAddSite() {
      this.showCreateDialog = true;
    },
    closeCreateDialog() {
      this.showCreateDialog = false;
      this.newSiteName = "";
      this.newSiteSubdomain = "";
      this.newSiteSubdomainTouched = false;
      this.newSiteSlug = "";
      this.createError = "";
    },
    cleanNewSiteSubdomain() {
      this.newSiteSubdomain = normalizeVisitorSubdomain(this.newSiteSubdomain);
    },
    validateNewSiteSubdomain() {
      this.cleanNewSiteSubdomain();
      if (!this.newSiteSubdomain) return "Visitor subdomain is required.";
      if (!isValidVisitorSubdomain(this.newSiteSubdomain)) return 'Use one subdomain name, for example "quin".';
      if (isReservedVisitorSubdomain(this.newSiteSubdomain)) return "That subdomain is reserved.";
      return "";
    },
    createErrorMessage(err) {
      return err.response?.data?.message || err.message || "Create failed. Please try again.";
    },
    async submitCreateSite() {
      const name = (this.newSiteName || "").trim();
      if (!name) {
        this.createError = "Site name is required.";
        return;
      }
      const subdomainError = this.validateNewSiteSubdomain();
      if (subdomainError) {
        this.createError = subdomainError;
        return;
      }

      this.createBusy = true;
      this.createError = "";

      try {
        const hostname = buildVisitorHostname(this.newSiteSubdomain);
        const created = await createSite({
          name,
          slug: (this.newSiteSlug || "").trim() || null,
        });
        try {
          await addSiteHostname(created.id, hostname);
        } catch (hostnameErr) {
          try {
            await deleteSite(created.id);
          } catch {
            this.sites = [created, ...this.sites];
            this.createError = `${this.createErrorMessage(hostnameErr)} The site was created, but the visitor address could not be assigned.`;
            return;
          }
          throw hostnameErr;
        }
        this.sites = [created, ...this.sites];
        this.closeCreateDialog();
      } catch (err) {
        console.error("Error creating site:", err);
        this.createError = this.createErrorMessage(err);
      } finally {
        this.createBusy = false;
      }
    },
    onViewDetails(siteId) {
      // make sure siteId is defined!
      if (!siteId) {
        console.warn("onViewDetails called without a siteId");
        return;
      }
      this.$router.push({ name: "Tours", params: { id: String(siteId) } });
    },
    async onDeleteSite(site) {
      const siteId = site?.id ?? site?.Id;
      if (!siteId) {
        console.warn("onDeleteSite called without a siteId");
        return;
      }
      let tours = [];
      try {
        tours = await fetchTours(siteId);
      } catch {
        await showMessage(`Could not check the tours for "${site.name || site.Name || "this site"}". Please retry before deleting the site.`);
        return;
      }
      if (tours.length) {
        await showMessage(`Delete the ${tours.length === 1 ? "tour" : "tours"} at "${site.name || site.Name || "this site"}" before deleting the site.`);
        return;
      }
      if (!(await showConfirm(`Delete "${site.name || site.Name || "this site"}"?`))) return;

      try {
        await deleteSite(siteId);
        this.sites = this.sites.filter((s) => (s.id ?? s.Id) !== siteId);
      } catch (err) {
        console.error("Error deleting site:", err);
        await showMessage(err.response?.data?.message || "Could not delete the site. Please delete its tours first, then try again.");
      }
    },
  },
};
</script>
<style scoped>
.site-card {
  display: flex;
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
}
.card-details {
  flex: 0 0 41.6667%;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}
.card-map {
  flex: 1 1 0;
  position: relative;
  display: flex;
  overflow: hidden;
  z-index: 0;
}
.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.card-map .map-container {
  width: 100% !important;
  height: 100% !important;
  border-radius: 0;
}
.text-h4 {
  font-size: 1.5rem;
  line-height: 2rem;
}
.font-weight-bold {
  font-weight: 700 !important;
}
.font-weight-medium {
  font-weight: 500 !important;
}
.caption {
  font-size: 0.75rem;
  line-height: 1rem;
}
.grey--text {
  color: #757575 !important;
}
.subtitle-1 {
  font-size: 1rem;
  line-height: 1.5rem;
  color: #212121;
}
.body-2 {
  width: 200px;
  font-size: 0.9rem;
  line-height: 1.25rem;
}
.subtitle-2 {
  font-size: 0.875rem;
}

.coverImage{
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.coverPlaceholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #757575;
  font-size: 0.9rem;
  background: #f5f5f5;
}

@media (max-width: 1100px) {
  .card-actions {
    gap: 6px;
  }
  .card-actions .v-btn {
    padding: 4px 8px;
    font-size: 12px;
  }
}
</style>

